<template>
    <div class="sub-assignment-page">
        <div
            class="hidden-anchor"
            :id="'page-' + pageIn.id"></div>
        <div
            class="page-container"
            role="tablist">
            <div
                class="page-header"
                role="tab">
                <b-button
                    class="btn-page-content-toggle"
                    block
                    variant="outline-primary"
                    @click="collapse()"
                    :class="collapsed && 'active'">
                    <div class="title">
                        <div
                            class="page-title"
                            v-if="pageIn.name">
                            {{ pageIn.name }}
                        </div>

                        <div
                            class="page-title"
                            v-else>
                            Pagina {{ pageIn.order }}
                        </div>
                    </div>

                    <i class="fas fa-fw fa-chevron-down state-icon"></i>

                    <div class="context-nav context-sub-assignment-page-nav">
                        <b-dropdown
                            class="ellipsis-nav sub-assignment-page-nav"
                            no-caret
                            variant="primary"
                            right
                            toggle-class="sub-assignment-page-context-menu">
                            <template v-slot:button-content>
                                <i class="fas fa-ellipsis-v"></i>
                            </template>

                            <b-dropdown-item
                                class="btn-edit edit-sub-assignment-page"
                                @click.prevent.stop="editName(pageIn, $event)">
                                {{ $t('assignment.page.edit') }}
                            </b-dropdown-item>

                            <b-dropdown-item
                                class="remove-sub-assignment-page"
                                @click.prevent.stop="onRemove($event)">
                                <i class="btn-remove"></i>{{ $t('assignment.page.delete') }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>

                    <design-2021-lesson-module-assignment-edit-page-modal
                        :page-in="pageIn"
                        @sub-assignment-updated="updateSubAssignment"
                        :ref="'assignment-edit-page' + pageIn.id">
                    </design-2021-lesson-module-assignment-edit-page-modal>
                </b-button>
            </div>

            <b-collapse
                class="page-content"
                v-model="collapsed"
                :id="'collapse-page-' + pageIn.id"
                role="tabpanel"
                visible>
                <draggable
                    group="parts"
                    :list="pageIn.parts"
                    handle=".sub-assignment-part-explanation-drag-handle"
                    @change="orderChanged()"
                    ghost-class="drag-ghost">
                    <div
                        v-for="part in pageIn.parts"
                        :class="part.type === 1 ? 'explanation' : 'assignment'"
                        :key="part.id">
                        <design-2021-lesson-module-assignment-explanation-part-component
                            v-if="part.type === 1"
                            :page-in="pageIn"
                            :part-in="part"
                            @part-added="onPartAdded"
                            @part-updated="updatePart"
                            @sub-assignment-updated="updateSubAssignment">
                        </design-2021-lesson-module-assignment-explanation-part-component>
                        <design-2021-lesson-module-assignment-part-component
                            v-else
                            :page-in="pageIn"
                            :part-in="part"
                            @part-added="onPartAdded"
                            @part-updated="updatePart"
                            @sub-assignment-updated="updateSubAssignment">
                        </design-2021-lesson-module-assignment-part-component>
                    </div>
                </draggable>

                <div class="add-assignment-part">
                    <b-button
                        class="btn-add btn-add-assignment-part"
                        v-b-tooltip="'Nieuwe onderdeel'"
                        v-b-modal="'add-assignment-part-modal-' + pageIn.id"
                        variant="primary">
                        {{ $t('assignment.add_part') }}
                        <design-2021-lesson-module-assignment-add-part-modal-component
                            @part-added="onPartAdded($event)"
                            :page-in="pageIn">
                        </design-2021-lesson-module-assignment-add-part-modal-component>
                    </b-button>
                </div>
            </b-collapse>
        </div>
    </div>
</template>

<script>
import draggable from 'vuedraggable';

export default {
    name: 'design-2021-lesson-module-assignment-page-component',
    components: {
        draggable,
    },
    props: {
        pageIn: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            collapsed: true,
        };
    },
    methods: {
        editName(page, event) {
            event.stopPropagation();

            this.$refs['assignment-edit-page' + page.id].show(page);
        },
        collapse() {
            this.collapsed = !this.collapsed;
            const name = 'assignment-edit-page-collapse-' + this.pageIn.id;
            localStorage.setItem(name, this.collapsed ? 1 : 0);
        },
        onPartAdded(newPart) {
            let page = this.pageIn;
            page.parts.push(newPart);

            this.$emit('page-updated', page);

            Vue.nextTick(() => {
                const name =
                    newPart.type === 1
                        ? 'update-explanation-part-model-' + newPart.id
                        : 'update-assignment-part-model-' + newPart.id;
                this.$bvModal.show(name);
            });
        },
        updatePart(newPart) {
            let page = this.pageIn;
            page.parts = page.parts.map((part) => {
                if (part.id === newPart.id) {
                    return newPart;
                }
                return part;
            });

            this.$emit('page-updated', page);
        },
        partRemoved(partId) {},
        onRemove(event) {
            event.stopPropagation();

            const text = this.pageIn.parts.length
                ? this.$t('assignment.confirm_delete_page_and_parts')
                : this.$t('assignment.confirm_delete_page');

            this.alert.confirm(this.$t('assignment.confirm_delete_page_title'), text).then((confirm) => {
                if (!confirm) {
                    return;
                }

                this.alert.saving();
                this.axios.delete(this.routes.assignmentPageRemove(this.pageIn.id)).then(
                    (response) => {
                        this.alert.success();
                        this.$emit('page-removed', this.pageIn.id);
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
            });
        },
        orderChanged() {
            let x = 1;
            let blocks = [];

            for (const part of this.pageIn.parts) {
                blocks.push({
                    id: part.id,
                    order: x,
                });
                x++;
            }

            const data = {
                page_id: this.pageIn.id,
                sub_assignment_id: this.pageIn.sub_assignment_id,
                blocks: blocks,
            };

            this.axios.post(this.routes.assignmentPartOrder(), data).then(
                (response) => {
                    this.$emit('sub-assignment-updated', response.data);
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
        updateSubAssignment(newSubAssignment) {
            this.$emit('sub-assignment-updated', newSubAssignment);
        },
    },
};
</script>

<style scoped lang="scss">
.hidden-anchor {
    position: relative;
    top: -110px;
    visibility: hidden;
}

.page-header {
    padding: 0;
    margin-bottom: 0;
    min-height: unset;
}

.sub-assignment-page {
    margin-bottom: map-get($grid-gutter-spacing, 'fifteen');

    .btn-page-content-toggle {
        justify-content: space-between;
        border: 1px solid var(--primary);
        outline: none;
        box-shadow: none;
        border-radius: $border-radius-content;
        line-height: 1;

        &,
        .title {
            display: flex;
            flex-direction: row;
            align-items: center;
        }

        .state-icon {
            transform-origin: center;
            transition: all 0.8s ease;
            margin-left: auto;
            margin-right: map-get($grid-gutter-spacing, 'fifteen');
        }

        &:hover {
            background-color: $info;
            font-weight: $font-weight-bold;

            &,
            .sub-assignment-drag-handle {
                color: $white;
            }
        }

        &.active {
            background-color: $info;
            font-weight: $font-weight-bold;
            border: 1px solid $info;

            .state-icon {
                transform: rotate(-180deg);
            }
        }
    }

    .page-content {
        margin-top: map-get($grid-gutter-spacing, 'fifteen');
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');

        div:first-child {
            > div:last-child {
                .assignment-question-header {
                    border-bottom-color: transparent;
                }
            }
        }

        .assignment ::v-deep {
            flex: 1 0 100%;

            .context-assignment-part-nav {
                position: relative;
                right: -5px;
            }
        }

        .explanation,
        .assignment {
            display: flex;
            flex-direction: row;
            align-items: center;
            margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
            padding-right: map-get($grid-gutter-spacing, 'fifteen');
            padding-left: map-get($grid-gutter-spacing, 'fifteen');

            .drag-handle {
                margin-right: map-get($grid-gutter-spacing, 'fifteen');
                align-self: center;
                font-size: $base-font-size;
                color: var(--primary);

                &:hover {
                    cursor: pointer;
                }
            }
        }

        .explanation {
            .drag-handle {
                margin-left: 9px;
            }
        }

        .add-assignment-part {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-top: map-get($grid-gutter-spacing, 'forty-two');
            margin-bottom: map-get($grid-gutter-spacing, 'forty-two');

            .btn {
                width: 185px;
            }
        }
    }
}

.context-sub-assignment-page-nav ::v-deep {
    .sub-assignment-page-nav {
        .sub-assignment-page-context-menu {
            line-height: 1;
        }

        .dropdown-menu {
            border: 1px solid $border-color;
        }

        .edit-sub-assignment-page {
            &:before {
                position: relative;
                left: 3px;
                width: 20px;
                margin-right: 12px;
            }
        }

        .copy-sub-assignment-page {
            i {
                color: var(--primary);
            }
        }

        .remove-sub-assignment-page {
            i {
                color: $danger;
            }
        }

        .remove-sub-assignment-page,
        .copy-sub-assignment-page {
            font-size: $default-font-size;

            .dropdown-item {
                display: flex;
                align-items: center;

                i {
                    margin-right: map-get($grid-gutter-spacing, 'eight');
                    font-size: $base-font-size;
                }
            }
        }
    }
}
</style>
